import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Smartphone, Wallet, Zap } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

const PLAY_STORE_URL = 'https://get.bucktime.ca/android?src=about';
const APP_STORE_URL = 'https://get.bucktime.ca/ios?src=about';

export const AboutPage = () => {
  return (
    <div className="landing-page" data-testid="about-page">
      <SiteHeader />

      <section className="page-content-section">
        <div className="container page-container-inner">
          <h1 className="page-main-title" data-testid="about-title">About BuckTime!</h1>
          <p className="page-subtitle">
            BuckTime! is a choice tool, not a blocker. The day's money is your child's from the morning — the apps you choose are the only thing that spends it. What they don't spend, they keep.
          </p>

          <div className="about-story" data-testid="about-story">
            <Card className="feature-card">
              <CardContent className="feature-content">
                <h2 className="feature-title">The Problem</h2>
                <p className="feature-description">
                  Kids are spending more time than ever on their phones. Parents are stuck in a cycle of nagging, negotiating, and arguing about device use. Blocking apps feels punitive, and kids push back. There had to be a better way.
                </p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent className="feature-content">
                <h2 className="feature-title">Our Approach</h2>
                <p className="feature-description">
                  BuckTime! is a choice tool, not a blocker. Your child can always open a monitored app — doing so simply spends their day's money. We never block another app from opening, never lock the device, and never take the phone away. The choice is theirs.
                </p>
              </CardContent>
            </Card>
          </div>

          <h2 className="about-section-heading" data-testid="about-how-heading">How It Works</h2>

          <div className="about-features">
            <div className="about-feature-item" data-testid="about-feature-0">
              <div className="about-feature-icon">
                <Smartphone className="w-8 h-8" />
              </div>
              <div>
                <h3 className="about-feature-title">Set what a day is worth.</h3>
                <p className="feature-description">You decide the daily amount, and how much time on those apps costs nothing at all.</p>
              </div>
            </div>

            <div className="about-feature-item" data-testid="about-feature-1">
              <div className="about-feature-icon icon-golden">
                <Zap className="w-8 h-8" />
              </div>
              <div>
                <h3 className="about-feature-title">Your child starts the day holding it.</h3>
                <p className="feature-description">The full amount is theirs from the morning. The apps you picked spend it — nothing is blocked, it's their call.</p>
              </div>
            </div>

            <div className="about-feature-item" data-testid="about-feature-2">
              <div className="about-feature-icon icon-lime">
                <Wallet className="w-8 h-8" />
              </div>
              <div>
                <h3 className="about-feature-title">They keep what they don't spend.</h3>
                <p className="feature-description">BuckTime! tracks what your child keeps; you pay them yourself — in cash, by e-Transfer from your own bank, or however you already do it. No bank linking, no fees, no money moves through BuckTime!.</p>
              </div>
            </div>

            <div className="about-feature-item" data-testid="about-feature-3">
              <div className="about-feature-icon">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <h3 className="about-feature-title">Privacy by design.</h3>
                <p className="feature-description">Built for families and compliant with COPPA, GDPR and PIPEDA. We never sell your data and never share it with advertisers. Children's profiles are created and controlled only by the parent.</p>
              </div>
            </div>
          </div>

          <Card className="feature-card about-cta-card" data-testid="about-cta">
            <CardContent className="feature-content" style={{ textAlign: 'center' }}>
              <h2 className="feature-title">Available on Google Play</h2>
              <p className="feature-description" style={{ marginBottom: '0.5rem' }}>
                Basic is free — one child, one device, everything you need to start.
              </p>
              <div className="lp-hero-btns">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-play-badge"
                  data-testid="about-play-btn"
                >
                  <img src="/google-play-badge-trimmed.png" alt="Get it on Google Play" className="play-badge-img" style={{width: '200px', height: '60px', objectFit: 'contain'}} />
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-play-badge"
                  data-testid="about-appstore-btn"
                >
                  <img src="/app-store-badge.svg" alt="Download on the App Store" className="play-badge-img" style={{width: '200px', height: '60px', objectFit: 'contain'}} />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;
